import { notificationAPI } from "../../../services/notificationAPI";
import { staffPortalApi } from "../../../services/StaffPortal/staffportalApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../StaffPortal/ConfirmationDialog";
import React, { useState, useEffect } from "react";
import { ArrowRight, AlertCircle } from "lucide-react";

const rejectionReasonsList = [
  "Property documents not clear",
  "Not satisfied with legal documents",
  "Higher active accounts",
  "Settlement amount",
  "Suit-file, written-off",
  "Irregular payments",
  "Business not valid",
  "No proper Tax filings",
  "No GST",
  "No Transactions statement",
];

const ReviewActions = ({ handleLoanStatusChange, application, documents }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [note, setNote] = useState("");
  const [documentsStatus, setDocumentsStatus] = useState({
    isComplete: false,
    message: "",
  });

  // Check documents status
  useEffect(() => {
    if (!documents || documents.length === 0) {
      setDocumentsStatus({
        isComplete: false,
        message: "No documents found",
      });
      return;
    }

    const REQUIRED_DOCUMENTS = {
      salaried: [
        "PAN_CARD",
        "AADHAR_CARD",
        "BANK_STATEMENT",
        "SALARY_SLIP",
        "LIVE_SELFIE",
        "CANCELED_CHEQUE",
      ],
      "self-employed": [
        "PAN_CARD",
        "AADHAR_CARD",
        "BANK_STATEMENT",
        "INCOME_TAX_RETURN",
        "LIVE_SELFIE",
        "CANCELED_CHEQUE",
      ],
      "business-owner": [
        "PAN_CARD",
        "AADHAR_CARD",
        "GST_DOCUMENT",
        "COMPANY_REGISTRATION_LETTER",
        "MSME_UDYAM_LICENSE",
        "MUNICIPAL_LICENSE",
        "RENTAL_AGREEMENT",
        "LIVE_SELFIE",
        "CANCELED_CHEQUE",
      ],
      default: [
        "PAN_CARD",
        "AADHAR_CARD",
        "BANK_STATEMENT",
        "SALARY_SLIP",
        "LIVE_SELFIE",
        "CANCELED_CHEQUE",
      ],
    };

    const employmentType = application?.user?.employmentType || "salaried";
    const requiredDocs =
      REQUIRED_DOCUMENTS[employmentType] || REQUIRED_DOCUMENTS.default;

    // Check if all required documents are verified
    const allRequiredVerified = requiredDocs.every((docType) => {
      if (docType === "SALARY_SLIP") {
        return documents.some(
          (doc) =>
            (doc.documentType === "SALARY_SLIPS" ||
              doc.documentType === "SALARY_SLIP") &&
            doc.status === "verified"
        );
      }
      return documents.some(
        (doc) => doc.documentType === docType && doc.status === "verified"
      );
    });

    // Check for rejected documents
    const hasRejectedDocs = documents.some((doc) => doc.status === "rejected");

    // Check for incomplete documents
    const hasIncompleteDocs = documents.some(
      (doc) => doc.status === "not-uploaded"
    );

    if (hasRejectedDocs) {
      setDocumentsStatus({
        isComplete: false,
        message: "Some documents were rejected and need to be re-uploaded",
      });
    } else if (hasIncompleteDocs) {
      setDocumentsStatus({
        isComplete: false,
        message: "Some documents are not uploaded",
      });
    } else if (!allRequiredVerified) {
      const missingDocs = requiredDocs.filter((docType) => {
        if (docType === "SALARY_SLIP") {
          return !documents.some(
            (doc) =>
              (doc.documentType === "SALARY_SLIPS" ||
                doc.documentType === "SALARY_SLIP") &&
              doc.status === "verified"
          );
        }
        return !documents.some(
          (doc) => doc.documentType === docType && doc.status === "verified"
        );
      });
      setDocumentsStatus({
        isComplete: false,
        message: `Missing verified documents: ${missingDocs.join(", ")}`,
      });
    } else {
      setDocumentsStatus({
        isComplete: true,
        message: "All required documents are verified",
      });
    }
  }, [documents, application]);

  const updateStatus = async (newStatus) => {
    await handleLoanStatusChange(newStatus);
  };

  const sendNotification = async (
    userId,
    note,
    notificationType,
    senderUserId
  ) => {
    try {
      const notification = await notificationAPI.sendNotification(
        userId,
        note,
        notificationType,
        senderUserId
      );
      toast.success(`Notification sent: ${note}`);
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Could not send notification.");
    }
  };

  const handleSendToCreditManager = async () => {
    try {
      const response = await staffPortalApi.assignLoanToCreditManager(
        application._id
      );
      toast.success("Sucessfully sent to Credit Manager");

      // if (response.success == true) {
      //   toast.success(response.message);
      // } else {
      //   toast.error("No Credit Manager Available");
      // }
      const creditManagerId = response?.creditManagerId;

      updateStatus("processing");

      await sendNotification(
        creditManagerId,
        "New application assigned to you.",
        "Staff",
        application?.assignedTo?._id
      );
    } catch (error) {
      console.error("Error assigning to credit manager:", error);
    }
  };

  const openConfirmDialog = () => {
    setIsDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsDialogOpen(false);
  };

  const confirmSendToCreditManager = () => {
    handleSendToCreditManager();
    closeConfirmDialog();
  };

  const toggleReason = (reason) => {
    setSelectedReason(reason);
  };

  const handleReject = async () => {
    const reasonText = selectedReason
      ? `Application rejected due to: ${selectedReason}`
      : "Application Rejected";
    setNote(reasonText);
    await handleLoanStatusChange("rejected", selectedReason);
    await sendNotification(
      application?.user?._id,
      reasonText,
      "Staff",
      application?.assignedTo?._id
    );
    setDropdownOpen(false);
    setSelectedReason("");
  };

  return (
    <>
      {dropdownOpen && (
        <div className="absolute bottom-full mb-2 z-5 border border-red-600 rounded-md p-2 w-full sm:w-[300px] bg-[#1e1e1e]">
          <h4 className="text-white mb-2 font-medium">
            Select Rejection Reason:
          </h4>
          <div className="max-h-48 overflow-y-auto">
            {rejectionReasonsList.map((reason) => (
              <label
                key={reason}
                className="flex items-start px-2 py-1 text-white hover:bg-red-800 rounded cursor-pointer"
              >
                <input
                  type="radio"
                  name="rejectionReason"
                  className="mr-2 mt-1 flex-shrink-0"
                  checked={selectedReason === reason}
                  onChange={() => toggleReason(reason)}
                />
                <span className="break-words">{reason}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col bg-[#131313] rounded-lg p-4 gap-4">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => {
                if (dropdownOpen) {
                  handleReject();
                } else {
                  setDropdownOpen(true);
                }
              }}
              className={`bg-red-500 text-white px-4 py-2 rounded-md flex items-center justify-center text-center ${
                dropdownOpen ? "w-full sm:w-[300px]" : "w-full sm:w-auto"
              }`}
            >
              <span className="mr-2">⨯</span>
              {dropdownOpen ? "Confirm Rejection 🚫" : "Reject Application"}
            </button>
            {dropdownOpen && (
              <button
                onClick={() => {
                  setDropdownOpen(false);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md flex items-center justify-center text-center w-full sm:w-auto"
              >
                <span className="mr-2">⨯</span> Cancel
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() =>
                sendNotification(
                  application?.user?._id,
                  `Please submit your documents`,
                  "Staff",
                  application?.assignedTo?._id
                )
              }
              className="bg-gray-800 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto"
            >
              Request Documents
            </button>

            {application?.status === "submitted" ||
            application?.status === "verified" ? (
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={async () => {
                    await updateStatus("in-review");
                    await sendNotification(
                      application?.user?._id,
                      "Your documents are being reviewed.",
                      "Credit Manager",
                      application?.assignedTo?._id
                    );
                  }}
                  disabled={!documentsStatus.isComplete}
                  className={`flex items-center justify-center px-4 py-2 rounded-md w-full sm:w-auto ${
                    documentsStatus.isComplete
                      ? "bg-green-500 text-white"
                      : "bg-gray-600 text-gray-300 cursor-not-allowed"
                  }`}
                >
                  Confirm for review <ArrowRight className="w-4 h-4 ml-2" />
                </button>
                {!documentsStatus.isComplete && (
                  <div className="absolute bottom-full mb-4 left-0 right-0 sm:w-64 sm:-left-8 sm:right-auto bg-black text-white text-sm p-2 rounded-md items-center hidden sm:flex">
                    <AlertCircle className="w-4 h-4 mr-2 text-[#FFCA20] flex-shrink-0" />
                    <span className="break-words">
                      {documentsStatus.message}
                    </span>
                  </div>
                )}
              </div>
            ) : application?.status === "in-review" ? (
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={openConfirmDialog}
                  // disabled={!documentsStatus.isComplete}
                  className={`flex items-center justify-center px-4 py-2 rounded-md w-full sm:w-auto ${
                    documentsStatus.isComplete
                      ? "bg-green-500 text-white"
                      : "bg-gray-600 text-gray-300 cursor-not-allowed"
                  }`}
                >
                  Send to Credit Manager
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
                {!documentsStatus.isComplete && (
                  <div className="absolute bottom-full mb-4 left-0 right-0 sm:w-64 sm:-left-8 sm:right-auto bg-black text-white text-sm p-2 rounded-md items-center hidden sm:flex">
                    <AlertCircle className="w-4 h-4 mr-2 text-[#FFCA20] flex-shrink-0" />
                    <span className="break-words">
                      {documentsStatus.message}
                    </span>
                  </div>
                )}
              </div>
            ) : documentsStatus.isComplete ? (
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => {
                    updateStatus("in-review");
                    sendNotification(
                      application?.user?._id,
                      "Your documents are being reviewed.",
                      "Staff",
                      application?.assignedTo?._id
                    );
                  }}
                  className="flex items-center justify-center px-4 py-2 rounded-md w-full sm:w-auto bg-green-500 text-white"
                >
                  Confirm for review <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            ) : (
              <div className="text-gray-400 text-sm">
                No action available for status: {application?.status}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmSendToCreditManager}
        title="Send to Credit Manager"
        message="Are you sure all the documents are verified and you want to send this application to the Credit Manager?"
      />
    </>
  );
};

export default ReviewActions;
